import { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getUserRole } from '../utils/firebaseAuth';
import { getModulesForRole, getPermissionsForRole } from '../config/roles';
import { getPreviewRole } from '../utils/previewRole';

/**
 * Hook to get current user's role, allowed modules, and permissions from Firestore.
 */
export const useUserRole = () => {
  const [user, setUser] = useState(auth.currentUser);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // Local UI preview: allow role without Firebase sign-in
      const preview = getPreviewRole();
      if (!user?.email) {
        if (preview) {
          if (!cancelled) {
            setRole(preview);
            setLoading(false);
          }
          return;
        }
        setRole(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const r = await getUserRole(user.email);
        if (!cancelled) {
          setRole(preview || r);
        }
      } catch (err) {
        console.error('useUserRole error:', err);
        if (!cancelled) setRole(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user?.email]);

  const modules = role ? getModulesForRole(role) : [];
  const permissions = role ? getPermissionsForRole(role) : {};
  const isAdmin = role === 'admin';
  const isConsultant = role === 'consultant';
  const hasAccess = role !== null; // Has some role = has access

  return {
    user,
    role,
    loading,
    modules,
    permissions,
    isAdmin,
    isConsultant,
    hasAccess,
  };
};
